import { MusicItem } from '@/api/aside/type'
import { MusicPlayTypeEnum, useMusicStore } from './store'
import { random } from '@/utils/number'
import { useI18n } from '@/i18n'
import DateUtils from '@/utils/DateUtils'
import Modal from '@/controller/Modal'

export const audioRef = () => {
  return document.querySelector('#musicAudio') as HTMLAudioElement
}

// 处理 顺序播放和单曲循环
const getNextIndexInSequence = (
  currentIndex: number,
  total: number,
  isNext: boolean
) => {
  if (isNext) {
    return currentIndex === total - 1 ? 0 : currentIndex + 1
  } else {
    return currentIndex === 0 ? total - 1 : currentIndex - 1
  }
}

// 处理随机播放
const getRandomIndex = (total: number, currentIndex: number) => {
  let randomIndex
  // 防止只有一首歌死循环
  if (total > 1) {
    // 防止随机到当前歌做循环随机，保证随机到的不是当前曲目
    do {
      randomIndex = Math.floor(random(total))
    } while (randomIndex === currentIndex)
  } else {
    randomIndex = 0
  }
  return randomIndex
}

// 变更音乐
export const changeMusic = (
  next = true,
  fromUserAction = true
): Promise<void> => {
  return new Promise((resolve) => {
    const { musicPlayType, myMusicList, musicPlay, currentMusicIndex } =
      useMusicStore()
    const myMusicLen = myMusicList?.length
    let _currentMusicIndex = currentMusicIndex
    if (
      musicPlayType === 'xhbf' ||
      (musicPlayType === 'dqxh' && fromUserAction) // 随机模式下切歌操作如果来自于用户
    ) {
      _currentMusicIndex = getNextIndexInSequence(
        currentMusicIndex,
        myMusicLen,
        next
      )
    } else if (musicPlayType === MusicPlayTypeEnum.sj) {
      _currentMusicIndex = getRandomIndex(myMusicLen, currentMusicIndex)
    }

    // 单曲循环非用户操作直接重复播放
    useMusicStore().currentMusicIndex = _currentMusicIndex

    if (musicPlay) {
      resolve()
    }
  })
}

export const onMousedown = (e: MouseEvent | TouchEvent) => {
  const { totalTime, progressMousePosition } = useMusicStore()

  const audio = e.target as HTMLAudioElement
  const secondToOnePercentPx = totalTime / audio.clientWidth
  useMusicStore().currentTime = Math.ceil(
    progressMousePosition * secondToOnePercentPx
  )
  useMusicStore().progressMouseDown = true
}

/**音乐进度条 松开 */
export const onMouseUpOrLeave = () => {
  const { currentTime, progressMouseDown } = useMusicStore()

  if (!progressMouseDown) return
  const audioElement = audioRef()
  audioElement.currentTime = currentTime
  useMusicStore().progressMouseDown = false
}

export const onMousemove = (e: MouseEvent | TouchEvent) => {
  let _offsetX = 0
  if (e instanceof MouseEvent) {
    _offsetX = e.offsetX <= 0.5 ? 0 : e.offsetX
  }
  if (e instanceof TouchEvent) {
    let currentTarget = e.currentTarget as HTMLElement
    let left = 0
    while (currentTarget !== null) {
      left += currentTarget.offsetLeft
      currentTarget = currentTarget.offsetParent as HTMLElement
    }
    _offsetX = Math.max(e.touches[0].pageX - left, 0)
  }

  const { progressMouseDown, totalTime } = useMusicStore()

  if (progressMouseDown) {
    const audio = e.target as HTMLAudioElement
    const secondToOnePercentPx = totalTime / audio.clientWidth
    useMusicStore().currentTime = Math.ceil(_offsetX * secondToOnePercentPx)
  }
  useMusicStore().progressMousePosition = _offsetX
}

/**暂停和播放 */
export const play = () => {
  const { musicPlay } = useMusicStore()
  // 设置播放状态
  if (!musicPlay) {
    playMusic()
  } else {
    pauseMusic()
  }
}

/**开始播放音乐 */
export const playMusic = () => {
  const { musicVolume, musicPlay, musicVolumeOff } = useMusicStore()
  const audioElement = audioRef()
  audioElement.volume = musicVolume
  audioElement.muted = musicVolumeOff

  const playMethod = () => {
    audioElement
      .play()
      .then(() => {
        useMusicStore().musicPlay = true
      })
      .catch(() => {
        const body = document.querySelector('body')
        body?.addEventListener(
          'click',
          () => {
            if (musicPlay) {
              playMusic()
            }
          },
          { once: true, capture: true }
        )
      })
  }
  playMethod()

  // 播放器异常，尝试重试，偶现audio加载失败
  const retry = () => {
    setTimeout(() => {
      playMethod()
    }, 100)
    audioElement.removeEventListener('error', retry)
  }
  audioElement.addEventListener('error', retry)
}

/**暂停播放 */
export const pauseMusic = () => {
  try {
    const audioElement = audioRef()
    audioElement.pause()

    useMusicStore().musicPlay = false
  } catch (error) {}
}

export const downloadMusic = (item: MusicItem, cb?: () => void) => {
  const { t } = useI18n()

  const { musicLoadingIds } = useMusicStore()
  useMusicStore().musicLoadingIds = [...musicLoadingIds, item.id]

  const _musicLoadingIds = musicLoadingIds.filter((_item) => _item !== item.id)
  const mp3 = new Audio(item.oss_url)
  if (navigator.userAgent.match(/iPhone|iPod|iPad/i) != null) {
    setTimeout(() => {
      //兼容ios 不会自动加载音频
      mp3.load()
    }, 0)
  }
  mp3.addEventListener('canplaythrough', () => {
    addMyMusic(item.id)
    useMusicStore().musicLoadingIds = _musicLoadingIds

    cb?.()
  })
  mp3.addEventListener('error', () => {
    useMusicStore().musicLoadingIds = _musicLoadingIds
    // 带有回调函数的是自动下载场景，不弹错误提示
    if (!cb) {
      Modal.message({
        type: 'error',
        content: t('lobby.aside.music.downloadError')
      })
    }
  })
}

const addMyMusic = (id: number) => {
  const { myMusicList, sysMusicList, updateMyMusicList } = useMusicStore()
  const item = sysMusicList.find((v) => v.id === id)

  if (item && !myMusicList.find((v) => v.id === id)) {
    updateMyMusicList([...myMusicList, item])
  }
}

/**时长转成文字 */
export const mathMusicTime = (seconds = 70) => {
  const time = DateUtils.moment.duration(seconds, 'seconds')
  const m = time.minutes()
  const s = time.seconds()
  return DateUtils.moment({ m, s }).format('mm:ss')
}

export const getStorageIds = () => {
  const storageIds = localStorage.getItem('myMusicList')

  if (storageIds) {
    const parsedIds = JSON.parse(storageIds)

    if (Array.isArray(parsedIds) && parsedIds.every(Number.isFinite)) {
      return [...new Set(parsedIds as number[])]
    }
  }

  return []
}
